import { useCallback, useEffect, useState } from 'react';

import { Box } from '@mantine/core';
import { useDebouncedValue, useDidUpdate, useIntersection } from '@mantine/hooks';
import useSWR, { mutate as globalMutate } from 'swr';

import { Project } from 'models';

import { ApiService } from 'services/ApiService';
import { ProjectService } from 'services/ProjectService';

import { useProjectListsContext } from './use-project-lists-context';

export type ProjectSearchFilters = {
    query?: string;
    team?: string | null;
    customer?: string | null;
    tag?: string | null;
    referenceDesigns?: boolean;
    companies?: string[];
    following?: boolean;
};

type PageData = {
    page: number;
    docs: Project[];
};

function useProjectSearch(filters?: ProjectSearchFilters) {
    const [debouncedQuery] = useDebouncedValue(filters?.query, 300);

    const effectiveFilters = { ...filters, query: debouncedQuery };
    const filtersKey = JSON.stringify(effectiveFilters || {});

    const [page, setPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [totalDocs, setTotalDocs] = useState<number | null>(null);
    const [pageData, setPageData] = useState<{ key: string; pages: PageData[] }>({ key: filtersKey, pages: [] });

    const getCacheKey = (pageNumber: number) => `projects-${filtersKey}-page-${pageNumber}`;

    const fetchPage = async (pageNumber: number) => {
        try {
            const url = ProjectService.getSearchUrl(pageNumber, effectiveFilters);
            const result = await ApiService.get<{ docs: Project[]; hasNextPage: boolean; totalDocs: number }>(url, {
                throwError: true,
            });
            return result;
        } catch (e) {
            console.warn('Error getting projects', e);
            return { docs: [], hasNextPage: false, totalDocs: 0 };
        }
    };

    const { data, isValidating } = useSWR(getCacheKey(page), () => fetchPage(page), {
        revalidateOnFocus: false,
    });

    useEffect(() => {
        setPage(1);
    }, [filtersKey]);

    useEffect(() => {
        if (!data) return;
        if (isValidating) return;

        setPageData((prev) => {
            const existingPages = prev.key === filtersKey ? prev.pages : [];
            const existingIndex = existingPages.findIndex((pd) => pd.page === page);
            const pages =
                existingIndex >= 0
                    ? existingPages.map((pd, index) => (index === existingIndex ? { page, docs: data.docs } : pd))
                    : [...existingPages, { page, docs: data.docs }];
            return { key: filtersKey, pages };
        });

        setHasNextPage(data.hasNextPage);
        setTotalDocs(data.totalDocs);
    }, [JSON.stringify(data), isValidating, page, filtersKey]);

    const loadMore = useCallback(() => {
        if (hasNextPage && !isValidating) {
            setPage((prev) => {
                return prev + 1;
            });
        }
    }, [hasNextPage, isValidating]);

    const { ref: scrollHintRef, entry } = useIntersection({
        threshold: 1,
    });

    useEffect(() => {
        if (entry?.isIntersecting && !isValidating && hasNextPage) {
            loadMore();
        }
    }, [entry?.isIntersecting, isValidating, hasNextPage, loadMore]);

    const ScrollRef = (
        <>
            {!isValidating && (hasNextPage || totalDocs === null) && (
                <Box ref={scrollHintRef} style={{ height: 100 }} />
            )}
        </>
    );

    const allProjects =
        pageData.key === filtersKey ? [...pageData.pages].sort((a, b) => a.page - b.page).flatMap((pd) => pd.docs) : [];

    const refetch = async () => {
        const pages = await Promise.all(
            pageData.pages.map(async (pd) => {
                const result = await globalMutate(getCacheKey(pd.page), fetchPage(pd.page), { revalidate: false });
                return { page: pd.page, docs: result?.docs ?? [] };
            }),
        );

        setPageData((prev) => (prev.key === filtersKey ? { key: filtersKey, pages } : prev));
    };

    const { version: listsVersion } = useProjectListsContext();

    useDidUpdate(() => {
        refetch().then();
    }, [listsVersion]);

    return {
        projects: allProjects,
        ScrollRef,
        isLoading: isValidating,
        loadedCount: allProjects.length,
        totalCount: totalDocs,
        hasNextPage,
        forceLoadMore: () => {
            setPage((prev) => {
                return prev + 1;
            });
        },
        mutate: refetch,
    };
}

export { useProjectSearch };
